import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TOrganizationalEntity } from 'evolve-ui/src/common/types/organizational-entity';

export enum AnadoluCampaignTemplateStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum AnadoluCommunicationOperationTypes {
  OPERATION = 'operation',
  MARKETING = 'marketing',
  MARKETING_OFFERS = 'marketing_offers',
}

export enum AnadoluSenderProfiles {
  GENERAL = 'general',
  PERSONAL = 'personal',
}

export type TAnadoluCampaignTemplateData = {
  templateId?: string;
  templateName?: string;
  profile?: AnadoluSenderProfiles;
  editable?: boolean;
  content?: string;
};

export type TAnadoluCampaignTemplate = {
  id?: string;
  type?: 'anadolu-campaign-template';
  name?: string;
  status?: AnadoluCampaignTemplateStatus;
  allowedUsers?: TOrganizationalEntity[];
  initiative?: TOrganizationalEntity;
  data?: TAnadoluCampaignTemplateData;
  communicationType?: AnadoluCommunicationOperationTypes;
  channel?: 'email' | 'sms';
  agency?: TOrganizationalEntity;
};

export type TAnadoluSendgridTemplate = {
  id?: string;
  html?: string;
  name?: string;
  plain?: string;
  subject?: string;
};

const baseUrl = `${process.env.GATSBY_CUSTOMERS_API_URL}/anadolu/campaign-templates`;

export const anadoluCampaignTemplatesApi = createApi({
  reducerPath: 'anadolu-campaign-templates',
  baseQuery: fetchBaseQuery({
    baseUrl,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['AnadoluCampaignTemplate'],
  endpoints: (builder) => ({
    getAnadoluCampaignTemplates: builder.query<
      Response<TAnadoluCampaignTemplate>,
      TQueryParams
    >({
      providesTags: ['AnadoluCampaignTemplate'],
      query: formatQueryParams,
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TAnadoluCampaignTemplate>(
          res as never,
          meta,
          args,
          true,
        ),
    }),
    getSingleAnadoluCampaignTemplate: builder.query<
      Response<TAnadoluCampaignTemplate>,
      { campaignTemplateId: string }
    >({
      providesTags: ['AnadoluCampaignTemplate'],
      query: ({ campaignTemplateId }) =>
        formatQueryParams({}, `${campaignTemplateId}`),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignTemplate>,
    }),
    getAnadoluSendgridTemplates: builder.query<
      Response<TAnadoluSendgridTemplate>,
      TQueryParams
    >({
      providesTags: ['AnadoluCampaignTemplate'],
      query: (params) => formatQueryParams(params, 'sendgrid'),
      transformResponse: parseJsonApiResponse<TAnadoluSendgridTemplate>,
    }),
    getSingleAnadoluSendgridTemplate: builder.query<
      Response<TAnadoluSendgridTemplate>,
      { templateId: string }
    >({
      providesTags: ['AnadoluCampaignTemplate'],
      query: ({ templateId }) =>
        formatQueryParams({}, `sendgrid/${templateId}`),
      transformResponse: parseJsonApiResponse<TAnadoluSendgridTemplate>,
    }),
    postAnadoluCampaignTemplate: builder.mutation<
      Response<TAnadoluCampaignTemplate>,
      Omit<TAnadoluCampaignTemplate, 'id'>
    >({
      invalidatesTags: ['AnadoluCampaignTemplate'],
      query: (params) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign-template' },
          Request.POST,
          '',
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignTemplate>,
    }),
    patchAnadoluCampaignTemplate: builder.mutation<
      Response<TAnadoluCampaignTemplate>,
      Partial<TAnadoluCampaignTemplate> & { campaignTemplateId: string }
    >({
      invalidatesTags: ['AnadoluCampaignTemplate'],
      query: ({ campaignTemplateId, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign-template' },
          Request.PATCH,
          campaignTemplateId,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignTemplate>,
    }),
    deleteAnadoluCampaignTemplate: builder.mutation<
      Response<TAnadoluCampaignTemplate>,
      { id: string }
    >({
      invalidatesTags: ['AnadoluCampaignTemplate'],
      query: ({ id, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign-template' },
          Request.DELETE,
          id,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignTemplate>,
    }),
  }),
});

export const {
  useGetAnadoluCampaignTemplatesQuery,
  useGetSingleAnadoluCampaignTemplateQuery,
  useGetAnadoluSendgridTemplatesQuery,
  useGetSingleAnadoluSendgridTemplateQuery,
  usePostAnadoluCampaignTemplateMutation,
  usePatchAnadoluCampaignTemplateMutation,
  useDeleteAnadoluCampaignTemplateMutation,
} = anadoluCampaignTemplatesApi;
